
import { Vue, Options } from "vue-class-component";

/**
 * MaskedPasswordInput
 * ---------------------------------------------------------
 * Custom password input that displays "*" as the mask character
 * (per design requirement) instead of the browser's native "•".
 *
 * Since native <input type="password"> cannot be restyled to use
 * a custom mask character, this component uses type="text" and
 * manually manages a masked display value while keeping the real
 * value in a separate internal field.
 *
 * Emits:
 *  - "update:modelValue" (string)  -> the real, unmasked password
 *  - "change" (string)             -> fired on every change, same value
 *
 * Props:
 *  - modelValue: string            -> v-model binding for the real value
 *  - placeholder: string
 *  - autocomplete: string
 *  - isError: boolean              -> shows red border + warning icon
 */
@Options({
  name: "MaskedPasswordInput",
  props: {
    modelValue: {
      type: String,
      default: ""
    },
    placeholder: {
      type: String,
      default: ""
    },
    autocomplete: {
      type: String,
      default: "current-password"
    },
    isError: {
      type: Boolean,
      default: false
    }
  },
  emits: ["update:modelValue", "change"]
})
class MaskedPasswordInput extends Vue {
  modelValue!: string;
  placeholder!: string;
  autocomplete!: string;
  isError!: boolean;

  // internal real value, kept in sync with modelValue prop
  internalValue = this.modelValue ?? "";
  showPassword = false;
  iconType: "eye" | "eye-slash" = "eye";

  get displayValue(): string {
    return this.showPassword
      ? this.internalValue
      : "*".repeat(this.internalValue.length);
  }

  // keep internal value in sync if parent resets modelValue externally
  // (e.g. clearing the form)
  watchModelValue() {
    if (this.modelValue !== this.internalValue) {
      this.internalValue = this.modelValue ?? "";
    }
  }

  created() {
    this.$watch("modelValue", this.watchModelValue);
  }

  toggleShow() {
    this.showPassword = !this.showPassword;
    this.iconType = this.showPassword ? "eye-slash" : "eye";
  }

  emitChange() {
    this.$emit("update:modelValue", this.internalValue);
    this.$emit("change", this.internalValue);
  }

  handlePaste(e: ClipboardEvent) {
    // Let the native paste happen into the text input, handleInput()
    // will pick up the resulting diff normally. No special-case needed
    // because paste is just a big "insert" at the cursor position.
  }

  handleInput(e: Event) {
    const input = e.target as HTMLInputElement;
    const newDisplayValue = input.value;
    const cursorPos = input.selectionStart ?? newDisplayValue.length;
    const oldDisplayValue = this.displayValue;

    if (this.showPassword) {
      // Not masked right now: display value === real value directly.
      this.internalValue = newDisplayValue;
      this.emitChange();
      return;
    }

    const lengthDiff = newDisplayValue.length - oldDisplayValue.length;

    if (lengthDiff > 0) {
      // Characters were inserted. They live in newDisplayValue at
      // [cursorPos - lengthDiff, cursorPos), but since this string is
      // masked ("*"), we can't read the real typed characters from it.
      // Instead we diff against the *unmasked* internalValue's length
      // to figure out where the insertion happened, then pull the
      // actual typed characters from the raw input event when possible.
      const insertStart = cursorPos - lengthDiff;
      const insertedRaw = this.extractInserted(input, insertStart, lengthDiff);

      this.internalValue =
        this.internalValue.slice(0, insertStart) +
        insertedRaw +
        this.internalValue.slice(insertStart);
    } else if (lengthDiff < 0) {
      // Characters were deleted (backspace/delete/cut/select+delete).
      const deleteCount = -lengthDiff;
      this.internalValue =
        this.internalValue.slice(0, cursorPos) +
        this.internalValue.slice(cursorPos + deleteCount);
    } else if (
      lengthDiff === 0 &&
      newDisplayValue.length !== oldDisplayValue.length
    ) {
      // Defensive fallback, should not normally hit this branch.
    } else if (newDisplayValue.length === 0) {
      this.internalValue = "";
    }

    this.emitChange();

    // Re-sync the native input's value to the freshly computed mask
    // and restore cursor position, since Vue re-renders `displayValue`
    // asynchronously and the DOM may briefly show the raw text/mismatch.
    this.$nextTick(() => {
      const el = this.$refs.inputEl as HTMLInputElement;
      if (el) {
        el.value = this.displayValue;
        el.setSelectionRange(cursorPos, cursorPos);
      }
    });
  }

  /**
   * Best-effort extraction of the actual typed/pasted characters.
   * The native `input` event's `data` property contains the inserted
   * text for most input types (typing, IME, paste, drag-drop) in
   * modern browsers. We fall back to a placeholder character only if
   * that information is unavailable, since we cannot reconstruct real
   * characters out of a fully-masked display string.
   */
  extractInserted(
    input: HTMLInputElement,
    start: number,
    count: number
  ): string {
    const nativeEvent = (window.event as InputEvent) || undefined;
    if (
      nativeEvent &&
      typeof nativeEvent.data === "string" &&
      nativeEvent.data.length === count
    ) {
      return nativeEvent.data;
    }
    // Fallback: cannot recover exact characters (should be rare in
    // modern browsers). This prevents a crash but may produce an
    // incorrect real value in this edge case — logged for visibility.
    // eslint-disable-next-line no-console
    console.warn(
      "MaskedPasswordInput: could not reliably read inserted characters, falling back."
    );
    return input.value.slice(start, start + count);
  }
}

export default MaskedPasswordInput;
